import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Banknote,
  TrendingUp,
  RefreshCw,
  Sparkles,
  ShieldCheck,
  Bell,
  ArrowRight,
  AtSign,
  Send,
  QrCode,
  Bot,
  Globe2,
  CheckCircle2,
  LineChart as LineChartIcon,
} from "lucide-react";
import StoreBadges from "@/components/brand/StoreBadges";
import { EMBER_LOGO } from "@/components/brand/Logo";
import Seo from "@/components/seo/Seo";

export default function Ember360() {
  const { t } = useTranslation("products");
  const FEATURES = [
    { icon: Banknote, title: t("ember.features.unifiedTitle"), body: t("ember.features.unifiedBody") },
    { icon: TrendingUp, title: t("ember.features.portfolioTitle"), body: t("ember.features.portfolioBody") },
    { icon: RefreshCw, title: t("ember.features.subsTitle"), body: t("ember.features.subsBody") },
    { icon: Sparkles, title: t("ember.features.aiTitle"), body: t("ember.features.aiBody") },
    { icon: Bell, title: t("ember.features.cashflowTitle"), body: t("ember.features.cashflowBody") },
    { icon: ShieldCheck, title: t("ember.features.securityTitle"), body: t("ember.features.securityBody") },
  ];
  return (
    <>
      <Seo
        title={t("ember.seoTitle")}
        description={t("ember.seoDescription")}
        path="/ember"
        preloadImages={[
          "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/ikby14f6_Screenshot%202.webp",
        ]}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            "name": "Ember360",
            "operatingSystem": "iOS, Android",
            "applicationCategory": "FinanceApplication",
            "url": "https://atlasforgefinancial.com/ember",
            "downloadUrl": [
              "https://apps.apple.com/us/app/ember360/id6796043696",
              "https://play.google.com/store/apps/details?id=com.atlasforge.ember360",
            ],
            "installUrl": "https://apps.apple.com/us/app/ember360/id6796043696",
            "publisher": { "@type": "Organization", "name": "AtlasForge Financial" },
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          },
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Ember360 Pro",
            "description":
              "Ember360 Pro subscription — unlimited Ember AI, Insights Hub, and Plaid-linked accounts.",
            "brand": { "@type": "Brand", "name": "Ember360" },
            "offers": {
              "@type": "AggregateOffer",
              "priceCurrency": "USD",
              "lowPrice": "9.99",
              "highPrice": "89.99",
              "offerCount": 2,
              "availability": "https://schema.org/InStock",
            },
          },
        ]}
      />
      <section className="relative overflow-hidden bg-[#0A1128] text-white">
        <div className="absolute inset-0 af-ember-glow pointer-events-none opacity-60" />
        <div className="absolute inset-0 af-grid-bg opacity-[0.04]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 pb-24 md:pt-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <img
                src={EMBER_LOGO}
                alt="Ember360"
                data-testid="ember-product-logo"
                width="480"
                height="240"
                decoding="async"
                fetchPriority="high"
                className="h-52 md:h-64 lg:h-72 w-auto object-contain mb-6"
                style={{ mixBlendMode: "screen" }}
              />
              <span
                data-testid="ember-eyebrow"
                className="inline-flex items-center gap-2 rounded-full border border-[#FF5A36]/40 bg-[#FF5A36]/10 px-3 py-1.5 text-[12px] font-semibold text-[#FF8E73]"
              >
                <span className="h-2 w-2 rounded-full bg-[#FF5A36] af-pulse-dot" style={{ color: "#FF5A36" }} />
                {t("ember.eyebrow")}
              </span>
              <h1
                data-testid="ember-hero-title"
                className="font-display mt-6 text-[44px] sm:text-[56px] lg:text-[72px] leading-[1.02]"
              >
                {t("ember.heroTitle")}
              </h1>
              <p className="mt-6 text-[17px] leading-relaxed text-slate-300 max-w-xl">
                {t("ember.heroBody")}
              </p>

              <div className="mt-9">
                <StoreBadges product="ember-hero" />
              </div>

              <div className="mt-10 flex items-center gap-6 text-[13px] text-slate-400">
                <span className="inline-flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  Plaid-secured connections
                </span>
                <span className="inline-flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  AI insights & forecasting
                </span>
              </div>
            </div>

            {/* Hero phone — real app screenshot */}
            <div className="lg:col-span-5 relative">
              <div className="mx-auto max-w-[340px]">
                <div
                  data-testid="ember-hero-phone"
                  className="relative rounded-[42px] bg-gradient-to-br from-white/10 to-white/[0.02] p-1.5 shadow-2xl ring-1 ring-white/10 backdrop-blur"
                >
                  <img
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/ikby14f6_Screenshot%202.webp"
                    alt="Ember360 Home screen — total bank balance, quick actions Send / Receive / Link Bank / Ember AI, and linked accounts"
                    fetchPriority="high"
                    decoding="async"
                    width="340"
                    height="737"
                    className="rounded-[36px] w-full block"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">One app, many superpowers</p>
            <h2 className="font-display mt-3 text-[36px] md:text-[46px] leading-[1.05] text-[#0A1128]">
              Built for how you actually use money.
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-slate-200 p-7 hover:-translate-y-1 transition-transform af-shadow-card bg-white">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFF0ED]">
                  <Icon className="h-5 w-5 text-[#E03C1A]" />
                </span>
                <h3 className="font-display mt-5 text-[19px]">{title}</h3>
                <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* See it in action — real app screenshots */}
      <section id="screenshots" className="relative bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="max-w-3xl">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">See it in action</p>
            <h2 className="font-display mt-3 text-[36px] md:text-[46px] leading-[1.05] text-[#0A1128]">
              One app. Every account. Every market.
            </h2>
            <p className="mt-5 text-[15px] text-slate-600 leading-relaxed max-w-2xl">
              Live captures from Ember360 on iOS. From bank aggregation to
              Ember AI to crypto markets — this is the app, not a mockup.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                src: "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/ikby14f6_Screenshot%202.webp",
                title: "Home",
                caption: "Total bank balance across all linked accounts, plus quick actions for Send, Receive, Link Bank, and Ember AI.",
                testid: "ember-shot-home",
              },
              {
                src: "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/pj91wmmn_Screenshot%203.webp",
                title: "Ember AI",
                caption: "Chat about spending, balances, transactions, and markets — powered by Claude Sonnet 4.5.",
                testid: "ember-shot-ai",
              },
              {
                src: "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/xp9ku9dp_Screenshot%204.webp",
                title: "Accounts",
                caption: "Link banks read-only via Plaid. Filter by checking, savings, or investment.",
                testid: "ember-shot-accounts",
              },
              {
                src: "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/kp5bhalq_Screenshot%205.webp",
                title: "Markets",
                caption: "Search 10,000+ crypto assets and stocks. See rank, price, and live moves in one place.",
                testid: "ember-shot-markets",
              },
            ].map((s) => (
              <figure key={s.title} data-testid={s.testid} className="group">
                <div className="relative rounded-[28px] bg-gradient-to-br from-[#0A1128] to-[#1E293B] p-1.5 shadow-2xl ring-1 ring-black/10 group-hover:-translate-y-1 transition-transform">
                  <img
                    src={s.src}
                    alt={`Ember360 ${s.title} screen`}
                    loading="lazy"
                    decoding="async"
                    className="rounded-[22px] w-full block"
                  />
                </div>
                <figcaption className="mt-4">
                  <p className="font-display text-[15px] text-[#0A1128]">{s.title}</p>
                  <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
                    {s.caption}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Plans & pricing */}
      <section id="pricing" className="relative bg-[#F7F9FC] border-y border-slate-200/70">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Plans &amp; pricing</p>
            <h2 className="font-display mt-3 text-[36px] md:text-[46px] leading-[1.05] text-[#0A1128]">
              One simple plan. Everything Ember360 has to offer.
            </h2>
            <p className="mt-5 text-[15px] text-slate-600 leading-relaxed">
              Free to download. Upgrade to Pro whenever you're ready — cancel anytime from the app.
            </p>
          </div>

          {/* Pro plan card */}
          <div className="mt-14 grid max-w-lg mx-auto">
            <div
              data-testid="ember-plan-pro"
              className="relative rounded-2xl border-2 border-[#FF5A36] bg-white p-8 shadow-xl ring-2 ring-[#FF5A36]/10"
            >
              <span className="absolute -top-2.5 left-6 rounded-full bg-[#FF5A36] text-white text-[10.5px] font-semibold uppercase tracking-wider px-2.5 py-0.5">
                Ember360 Pro
              </span>
              <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[#0A1128] text-white">
                <Sparkles className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-[26px] text-[#0A1128]">
                Everything, unlocked.
              </h3>
              <p className="mt-1 text-[13.5px] text-slate-600">
                Full Ember AI, all Insights Hub lenses, and unlimited linked accounts.
              </p>

              <div className="mt-6 flex items-baseline gap-2">
                <p className="font-display text-[46px] leading-none text-[#0A1128]">$9.99</p>
                <p className="text-[13.5px] text-slate-500">/ month</p>
              </div>
              <p className="mt-2 text-[13.5px] text-slate-600">
                or <strong className="text-[#0A1128]">$89.99</strong> / year{" "}
                <span className="text-[#E03C1A] font-semibold">(Save 25% annually)</span>
              </p>
              <p className="text-[11.5px] text-slate-400">$7.50 / mo billed yearly</p>

              <ul className="mt-6 space-y-2.5">
                {[
                  "Unlimited Ember AI chats (Claude Sonnet 4.5)",
                  "Insights Hub — 9 AI lenses on your money",
                  "Unlimited Plaid-linked bank accounts",
                  "Send &amp; Receive by @handle",
                  "Live markets — crypto &amp; stocks",
                  "Priority support",
                ].map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-2 text-[13.5px] text-slate-700 leading-snug"
                  >
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#FF5A36] shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: p }} />
                  </li>
                ))}
              </ul>

              <div className="mt-7 flex justify-center">
                <StoreBadges product="ember-plan-pro" align="center" />
              </div>
              <p className="mt-4 text-center text-[11.5px] text-slate-400">
                Cancel anytime in the app. Free tier available with limited AI usage.
              </p>
            </div>
          </div>

          {/* Pro features shared list */}
          <div className="mt-16 max-w-5xl mx-auto rounded-3xl border border-slate-200 bg-white p-8 md:p-10">
            <div className="text-center max-w-xl mx-auto">
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">
                Included with Pro
              </p>
              <h3 className="font-display mt-2 text-[26px] md:text-[32px] text-[#0A1128] leading-tight">
                Every feature in Ember360.
              </h3>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: <Banknote className="h-4 w-4" />,
                  title: "Total bank balance",
                  body: "Every linked account, converted to USD, in one live view.",
                },
                {
                  icon: <Send className="h-4 w-4" />,
                  title: "Send",
                  body: "Send money by @handle to anyone else on Ember360.",
                },
                {
                  icon: <QrCode className="h-4 w-4" />,
                  title: "Receive",
                  body: "Share a QR or your @handle to get paid in seconds.",
                },
                {
                  icon: <ShieldCheck className="h-4 w-4" />,
                  title: "Link Bank via Plaid",
                  body: "Read-only bank connections through Plaid's secure sandbox and prod.",
                },
                {
                  icon: <Bot className="h-4 w-4" />,
                  title: "Ember AI",
                  body: "Ask about spending, balances, transactions, or markets — Claude Sonnet 4.5.",
                },
                {
                  icon: <Sparkles className="h-4 w-4" />,
                  title: "Insights Hub",
                  body: "9 AI lenses on your money — spending, budgets, net worth, and more.",
                },
                {
                  icon: <LineChartIcon className="h-4 w-4" />,
                  title: "Markets",
                  body: "Search 10,000+ crypto assets and stocks. Rank, price, and live moves.",
                },
                {
                  icon: <AtSign className="h-4 w-4" />,
                  title: "@handle profile",
                  body: "Your unique @handle is your money identity — no account numbers.",
                },
                {
                  icon: <Globe2 className="h-4 w-4" />,
                  title: "Multi-currency",
                  body: "Balances auto-convert to your home currency across accounts.",
                },
              ].map((f) => (
                <div key={f.title} className="flex items-start gap-3">
                  <div className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-[#FFF0ED] text-[#E03C1A] shrink-0">
                    {f.icon}
                  </div>
                  <div>
                    <p className="font-display text-[15px] text-[#0A1128]">
                      {f.title}
                    </p>
                    <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
                      {f.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="rounded-3xl bg-[#0A1128] text-white p-10 md:p-16 relative overflow-hidden">
            <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#FF5A36]/30 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-display text-[36px] md:text-[46px] leading-[1.05]">
                  Get Ember360 today.
                </h2>
                <p className="mt-4 text-slate-300 max-w-md">
                  One app for spending, saving, and seeing your full financial
                  picture.
                </p>
                <Link
                  to="/safe-to-spend"
                  data-testid="ember-cta-safetospend"
                  className="mt-6 inline-flex items-center gap-2 text-[14px] font-medium text-[#FF8E73] hover:gap-3 transition-all"
                >
                  Looking for budgeting only? Try Safe to Spend 365
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div>
                <StoreBadges product="ember-bottom-cta" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
